import React, { useState } from 'react';
import arrowUp from '../../assets/images/chevron-up.svg';
import arrowDown from '../../assets/images/chevron-down.svg';
import Card from '../Card/Card';
import { BolderText } from '../BolderText/BolderText';
import Pill from '../Pill/Pill';
import IconButton from '../IconButton/IconButton';
import { Icon } from '../Icon/Icon';
import { ReviewablePendingBookDto } from '../../api/MeetingsAPI';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import { useTranslation } from 'react-i18next';
import { ButtonV2 } from "../ButtonV2/ButtonV2";
import { Copy, Check } from 'lucide-react';
import { toastService } from '../../services/ToastService';
import PaymentStatusToggle from '../PaymentStatusToggle/PaymentStatusToggle';
import PendingBookingUrgencyPill from '../PendingBookingUrgencyPill/PendingBookingUrgencyPill';
import BrideHistoryWarningIcon from '../BrideHistoryWarningIcon/BrideHistoryWarningIcon';
import { TooltipProvider } from '../TooltipV2/TooltipV2';

interface PendingMeetingCardProps {
  meeting: ReviewablePendingBookDto;
  onAction: (id: number, action: 'accept' | 'reject') => Promise<void>;
  onPaymentStatusChange?: (id: number, paid: boolean) => Promise<void>;
}

const PendingMeetingCard: React.FC<PendingMeetingCardProps> = ({ meeting, onAction, onPaymentStatusChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleBookingAction = async (action: 'accept' | 'reject') => {
    setIsLoading(true);
    try {
      await onAction(meeting.id, action);
    } catch (error) {
      console.error(`Error ${action}ing booking:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyLinkToClipboard = () => {
    const fullLink = `${window.location.origin}${meeting.trackingLink}`;
    navigator.clipboard.writeText(fullLink)
      .then(() => {
        setCopied(true);
        toastService.showToast(t("booking.pending.link_copied") || "Link copied!");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        toastService.showToast(t("booking.pending.link_error") || "Copy failed", "error");
      });
  };

  return (
    <Card>
      <>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 min-w-0">
            <BolderText text={meeting.customerName} />
            {meeting.brideHistoryWarning && (
              <TooltipProvider delayDuration={200}>
                <BrideHistoryWarningIcon reason={meeting.brideHistoryWarningReason} />
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-2">
            {meeting.requiresPayment && (
              <div className="flex items-center gap-2">
                <Pill 
                  color={meeting.paid ? "green" : "orange"} 
                  text={meeting.paid ? (t("booking.pending.paid") || "Paid") : (t("booking.pending.not_paid") || "Not Paid")} 
                />
                {onPaymentStatusChange && (
                  <PaymentStatusToggle
                    id={meeting.id}
                    paid={meeting.paid}
                    onPaymentStatusChange={(id, paid) => onPaymentStatusChange(Number(id), paid)}
                    size="sm"
                  />
                )}
              </div>
            )}
            <ButtonV2
              variant="outline"
              size="sm"
              className="h-8 flex items-center gap-1"
              onClick={copyLinkToClipboard}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="hidden sm:inline">{t("booking.pending.copy_link") || "Copy Link"}</span>
            </ButtonV2>
          </div>
        </div>
        <div className="text-sm text-gray-600">{meeting.meetingType}</div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>{meeting.date} at {meeting.time}</span>
          <PendingBookingUrgencyPill date={meeting.date} time={meeting.time} />
        </div>
        <div className="flex justify-end">
          <IconButton onClick={() => setIsExpanded(!isExpanded)} >
            <Icon url={isExpanded ? arrowUp : arrowDown} cssStyles="justify-end"/>
          </IconButton>
        </div>

        {isExpanded && (
          <div className="border-l-2 border-gray-200 pl-1">
            <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.email")}:</strong> {meeting.customerData?.email}</div>
            <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.phone")}:</strong> {meeting.customerData?.phone}</div>
            {meeting.customerData?.budgetRange && (
              <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.budget")}:</strong> {meeting.customerData?.budgetRange}</div>
            )}
            {meeting.customerData?.numberOfGuests && meeting.customerData?.numberOfGuests !== "0" && (
                <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.people")}:</strong> {meeting.customerData?.numberOfGuests}</div>
            )}
            {meeting.customerData?.size && (
              <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.size")}:</strong> {meeting.customerData?.size}</div>
            )}
            {meeting.customerData?.weddingDate && (
                <div className="text-sm text-gray-700"><strong>{t("booking.pending.form.wedding_date")}:</strong> {meeting.customerData?.weddingDate}</div>
            )}
            {meeting.customerData?.additionalNotes && (
              <div className="text-sm text-gray-700 mt-2">
                <strong>{t("booking.pending.form.special_comments") || "Comments"}:</strong>
                <div className="mt-1 p-2 bg-gray-50 rounded border-l-2 border-blue-200">
                  {meeting.customerData.additionalNotes}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-2 flex justify-between">
          <PrimaryButton 
            text={isLoading ? t("booking.pending.processing")! : t("booking.pending.accept")!}
            fn={() => handleBookingAction('accept')}
            isDisabled={isLoading}
          />
          <SecondaryButton 
            text={isLoading ? t("booking.pending.processing")! : t("booking.pending.reject")!}
            fn={() => handleBookingAction('reject')}
            isDisabled={isLoading}
          />
        </div>
      </>
    </Card>
  );
};

export default PendingMeetingCard; 